/**
 * The assessment as the website shows it: the three criteria with their score descriptions
 * (Bijlage A, article 7, shown on every campaign page before the contribution: 7.5) and what the
 * jury routes of Strapi return for one juror. Strapi decides everything; see src/services/jury.ts
 * and src/utils/jury.ts in pooh-next.
 */

export type Criterion = "a" | "b" | "c";

export interface CriterionText {
  key: Criterion;
  title: string;
  question: string;
  /** What a score of 1, 3 and 5 means (article 7.2 to 7.4). */
  scores: { 1: string; 3: string; 5: string };
}

export const CRITERIA: CriterionText[] = [
  {
    key: "a",
    title: "Aansluiting bij wat de uurgever belangrijk vindt",
    question: "In hoeverre gaat de inzending concreet en herkenbaar in op de punten van de uurgever?",
    scores: {
      1: "Gaat niet of alleen in algemene termen op de punten in.",
      3: "Gaat concreet op ten minste één punt in.",
      5: "Gaat concreet en persoonlijk op meerdere punten in en maakt duidelijk waarom juist deze deelnemer en deze uurgever bij elkaar passen.",
    },
  },
  {
    key: "b",
    title: "Wat de uurgever en/of het goede doel eraan hebben",
    question:
      "Wat kan het uur met deze deelnemer de uurgever en/of het goede doel brengen, en hoe concreet en geloofwaardig is dat uitgewerkt?",
    scores: {
      1: "Er wordt geen of alleen een vage bedoeling genoemd.",
      3: "Er wordt concreet genoemd wat de uurgever of het goede doel eraan heeft.",
      5: "Er wordt concreet en uitvoerbaar genoemd wat de uurgever en/of het goede doel eraan hebben, en de deelnemer laat zien hoe hij dat gaat aanpakken.",
    },
  },
  {
    key: "c",
    title: "Overtuigingskracht",
    question: "Hoe helder, persoonlijk en geloofwaardig is de inzending als geheel?",
    scores: {
      1: "De inzending is onduidelijk of onpersoonlijk.",
      3: "De inzending is helder en persoonlijk.",
      5: "De inzending is helder, persoonlijk en zo geloofwaardig dat de lezer de deelnemer voor zich ziet.",
    },
  },
];

export type JuryPhase = "open" | "jury" | "tie" | "confirming" | "done";

export interface JuryEntry {
  number: number;
  text: string;
  words: number | null;
  a: number | null;
  b: number | null;
  c: number | null;
  note: string;
}

export interface JuryView {
  phase: JuryPhase;
  campaign: { title: string; uurgever: string; charity: string; points: string[]; question: string };
  member: {
    name: string;
    isUurgever: boolean;
    /** The uurgever, the person the uurgever appointed, or a member of the jury pool. */
    seat?: "uurgever" | "delegate" | "pool";
    /** A pool member confirms before this moment, or another one is assigned; null once declared. */
    confirmBy?: string | null;
    declared: boolean;
    submitted: boolean;
    confirmed: boolean;
  };
  deadline: string | null;
  declarationText: string;
  /** Null until the juror has made the declaration. */
  entries: JuryEntry[] | null;
  missing: { reason: "unscored" | "note_missing"; entries: number[] } | null;
  tie: { numbers: number[]; vote: { order?: number[] | null; motivation?: string | null } | null } | null;
  outcome: {
    ranking: Array<{ number: number; total: number; a: number; b: number; c: number; place: number }>;
    selected: number[];
    motivation: string[];
    noteNeeded: boolean;
  } | null;
}

/** The total a juror gave an entry, or null while a criterion has no score. */
export function entryTotal(entry: Pick<JuryEntry, "a" | "b" | "c">): number | null {
  return entry.a != null && entry.b != null && entry.c != null ? entry.a + entry.b + entry.c : null;
}

/** How many entries have a score on all three criteria. */
export function scoredCount(entries: JuryEntry[]): number {
  return entries.filter((entry) => entryTotal(entry) !== null).length;
}

/** The numbers of the entries this juror scores highest (several when their totals are equal). */
export function highestNumbers(entries: JuryEntry[]): number[] {
  const totals = entries.map((entry) => ({ number: entry.number, total: entryTotal(entry) })).filter((t) => t.total !== null);
  if (totals.length === 0) return [];
  const best = Math.max(...totals.map((t) => t.total as number));
  return totals.filter((t) => t.total === best).map((t) => t.number);
}
